import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { Form, Button, Typography, message, Flex } from "antd";
import FileLinkModal from "@/components/modals/FileLinkModal";
import FileInput from "./components/FileInput";
import { createRequest } from "@/api/requests";
import GroupRenderer from "./components/GroupRenderer";
import { formatRubles } from "@/utils/fieldValueConverter";

const { Text } = Typography;

function normalizeSystemItem(x) {
  const p = x?.shortcut || x;
  const file_uuid = p?.file_uuid;
  const name = p?.original_name || p?.name || "file";
  if (!file_uuid) return null;
  return { file_uuid, name };
}

function upsertSystemFiles(form, fieldName, incoming) {
  const current = form.getFieldValue(fieldName) || [];

  const add = (Array.isArray(incoming) ? incoming : [incoming])
    .map(normalizeSystemItem)
    .filter(Boolean);

  const map = new Map(current.map((f) => [f.file_uuid, f]));

  for (const item of add) {
    map.set(item.file_uuid, item);
  }

  form.setFieldsValue({
    [fieldName]: Array.from(map.values()),
  });
}

const CONTACT_GROUP = {
  uuid: "contact_group",
  name: "Контактные данные",
  idx: -1,
  fields: [
    {
      uuid: "contact_name",
      name: "Ваши ФИО",
      type: "text",
      is_required: true,
      idx: 1,
    },
    {
      uuid: "contact_email",
      name: "Email",
      type: "text",
      is_required: true,
      idx: 2,
    },
    {
      uuid: "contact_phone",
      name: "Телефон",
      type: "text",
      is_required: false,
      idx: 3,
    },
  ],
};

const FILE_FIELD = {
  uuid: "file",
  name: "Вложение",
  type: "file",
  is_required: false,
};

const FlexibleRequestForm = ({ data, onSave, isPreview = false }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isAuthorized = !!data?.recipient_uuid;

  const [isSystemModalShow, setIsSystemModalShow] = useState(false);
  const [activeFileField, setActiveFileField] = useState(null);

  const fileInputRef = useRef(null);

  const handleSaveFromSystem = (payload) => {
    if (!activeFileField) return;

    upsertSystemFiles(form, activeFileField, payload);

    message.success("Файлы добавлены");
    setIsSystemModalShow(false);
  };

  const fieldDeps = {
    isAuthorized,
    fileInputRef,
    openSystemModal: (fieldName) => {
      setActiveFileField(fieldName);
      setIsSystemModalShow(true);
    },
    form,
  };

  useEffect(() => {
    if (!data) return;

    form.setFieldsValue({
      contact_name: data.user_name || "",
      contact_email: data.user_email || "",
    });
  }, [data, form]);

  const handleSubmit = async (values) => {
    setError(null);

    if (isPreview) {
      return;
    }

    const fieldMap = new Map();

    data?.groups?.forEach((group) => {
      group.fields.forEach((field) => {
        fieldMap.set(field.uuid, field);
      });
    });

    const formData = new FormData();
    const requestFormData = {};

    Object.entries(values).forEach(([key, value]) => {
      if (value === undefined || value === null) return;

      const isSystemField =
        key === "contact_name" ||
        key === "contact_email" ||
        key === "contact_phone";

      const field = fieldMap.get(key);

      // date / moment
      if (dayjs.isDayjs(value)) {
        if (field?.type === "date") {
          value = value.format("DD.MM.YYYY");
        }

        if (field?.type === "moment") {
          value = value.format("DD.MM.YYYY HH:mm");
        }
      }

      // checkbox
      if (typeof value === "boolean") {
        value = value ? "Да" : "Нет";
      }

      // money
      if (field?.type === "money") {
        value = formatRubles(value);
      }

      if (Array.isArray(value)) {
        const hasLocal = value.some((f) => f.originFileObj);
        const hasSystem = value.some((f) => f.file_uuid);

        if (hasLocal || hasSystem) {
          value.forEach((f) => {
            if (f.originFileObj) {
              formData.append("file", f.originFileObj);
            }

            if (f.file_uuid) {
              formData.append("file_uuids", f.file_uuid);
            }
          });

          return;
        }
      }

      // системные поля формы
      if (isSystemField) {
        formData.append(key, value);
      } else {
        requestFormData[key] = value;
      }
    });

    // обязательные
    formData.append("request_form_uuid", data.uuid);
    formData.append("unit_uuid", data.unit_uuid);
    formData.append(
      "time_zone",
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    );

    formData.append("requestFormData", JSON.stringify(requestFormData));

    try {
      const response = await createRequest(formData);
      onSave(response);
      form.resetFields();
    } catch (error) {
      setError(error.message || "Ошибка при отправке формы");
    }
  };

  const onFinish = async () => {
    setLoading(true);

    try {
      const values = await form.validateFields();
      await handleSubmit(values);
    } catch (e) {
      console.log("Ошибка валидации:", e);
    }

    setLoading(false);
  };

  const renderGroup = (group) => {
    if (!group.fields?.length) return null;

    return <GroupRenderer key={group.uuid} group={group} deps={fieldDeps} />;
  };

  const groups = useMemo(() => {
    return [CONTACT_GROUP, ...(data?.groups || [])].sort(
      (a, b) => a.idx - b.idx,
    );
  }, [data]);

  return (
    <Form form={form} layout="horizontal">
      {groups.map(renderGroup)}

      {data?.enable_attachments && (
        <Form.Item layout="vertical" label="Вложения" name="file">
          <FileField field={FILE_FIELD} deps={fieldDeps} />
        </Form.Item>
      )}

      <Flex gap={10} align="center">
        <Button
          type="primary"
          size="large"
          loading={loading}
          onClick={!isPreview ? () => onFinish() : null}
          disabled={isPreview}
        >
          Отправить
        </Button>

        {error && <Text type="danger">{error}</Text>}
      </Flex>

      {isSystemModalShow && (
        <FileLinkModal
          visible={isSystemModalShow}
          onCancel={() => setIsSystemModalShow(false)}
          onSave={handleSaveFromSystem}
        />
      )}
    </Form>
  );
};

export default FlexibleRequestForm;

const FileField = ({ field, deps }) => <FileInput field={field} deps={deps} />;
